import type { SubscriptionDetails } from "schemas/generated/billing";

import React from "react";

import { useModal } from "@editor/hooks/useModal";
import Shopify from "@svg/shopify";

import { Card } from "@replo/design-system/components/shadcn/core/card";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { format } from "date-fns";
import { FileText } from "lucide-react";
import { getV1BillingPlanInfoByTier } from "schemas/billing";

import ManagePlanMenu from "../shared/ManagePlanMenu";
import {
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfV1AdditionalShopsInSubscription,
  getPlanColorTheme,
  getPlanIcon,
} from "./v1BillingUtils";
import {
  V1PlanPricePerMonthBreakdownByProducts,
  V1PlanPricePerMonthIncludingAllProducts,
} from "./V1PlanPrice";

type BillingLimitsCardProps = {
  elementUsage: {
    current: number;
    maximum: number | null;
  };
  integrationUsage: {
    current: number;
    maximum: number | null;
  };
  activeSubscription: SubscriptionDetails;
  nextBillDate?: string;
  workspaceId: string;
  isWorkspaceOwner: boolean;
};

const BillingLimitsCard: React.FC<BillingLimitsCardProps> = ({
  elementUsage,
  integrationUsage,
  activeSubscription,
  nextBillDate,
  workspaceId,
  isWorkspaceOwner,
}) => {
  const modal = useModal();

  const planInfo = getV1BillingPlanInfoByTier(activeSubscription.v1BillingTier);
  const planColors = getPlanColorTheme(planInfo.tier);

  if (planInfo.tier === "free") {
    return null;
  }

  const activeSubscriptionAdditionalShops =
    getCountOfV1AdditionalShopsInSubscription(activeSubscription);
  const activeSubscriptionAdditionalPublishedElementBundles =
    getCountOfAdditionalPublishedElementBundlesInSubscription(
      activeSubscription,
    );

  const handleCancelPlan = () => {
    if (!isWorkspaceOwner) {
      return;
    }
    modal.closeModal({ type: "billingModal" });
    modal.openModal({
      type: "cancelPlan",
      props: {
        source: "direct.billingDashboard",
        reploVersionToCancel: "v1",
      },
    });
  };

  return (
    <Card className="flex-1 gap-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getPlanIcon(planInfo.tier, planColors.stroke)}
          <span className="text-lg font-semibold">{planInfo.displayName}</span>
          <Tag
            size="sm"
            variant="neutral"
            UNSAFE_className={twMerge(planColors.bg, planColors.text, "ml-2")}
          >
            Billed{" "}
            {activeSubscription.billingInterval === "yearly"
              ? "Annually"
              : "Monthly"}
          </Tag>
        </div>
        <ManagePlanMenu
          workspaceId={workspaceId}
          isWorkspaceOwner={isWorkspaceOwner}
          activeSubscription={activeSubscription}
          handleCancelPlan={handleCancelPlan}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div>
          <div className="text-2xl font-bold">
            <V1PlanPricePerMonthIncludingAllProducts
              plan={planInfo}
              billingInterval={activeSubscription.billingInterval}
              additionalShops={activeSubscriptionAdditionalShops}
              additionalPublishedElementBundles={
                activeSubscriptionAdditionalPublishedElementBundles
              }
              variant="header"
            />
          </div>
          {nextBillDate && (
            <div className="text-muted text-sm">
              Next charge on {format(new Date(nextBillDate), "MMMM d, yyyy")}
            </div>
          )}
        </div>
        <div>
          {(activeSubscriptionAdditionalShops > 0 ||
            activeSubscriptionAdditionalPublishedElementBundles > 0) && (
            <V1PlanPricePerMonthBreakdownByProducts
              plan={planInfo}
              billingInterval={activeSubscription.billingInterval}
              additionalShops={activeSubscriptionAdditionalShops}
              additionalPublishedElementBundles={
                activeSubscriptionAdditionalPublishedElementBundles
              }
              className="text-sm"
            />
          )}
        </div>
      </div>
      {[
        {
          title: "Shopify Integrations",
          icon: <Shopify fill="#1E293B" />,
          maximum: integrationUsage.maximum,
          current: integrationUsage.current,
        },
        {
          title: "Published Items",
          icon: <FileText size={16} />,
          maximum: elementUsage.maximum,
          current: elementUsage.current,
        },
      ].map((item) => (
        <div key={item.title} className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex flex-row items-center gap-2">
              {item.icon}
              <p className="text-default text-base font-medium">{item.title}</p>
            </div>

            <div className="text-muted text-base font-medium">
              {item.current} used
              <span className="text-placeholder">
                {" "}
                /{" "}
                {item.maximum !== null
                  ? `${item.maximum} included`
                  : "Unlimited"}
              </span>
            </div>
          </div>
          <Progress
            value={item.current}
            max={item.maximum ?? item.current}
            indicatorClassName={
              item.maximum && item.current >= 0.75 * item.maximum
                ? "bg-attention-muted"
                : ""
            }
          />
        </div>
      ))}
    </Card>
  );
};

export default BillingLimitsCard;
